import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'

import { editorCommandOf, isOverlayActive, isTypingTarget } from '@/lib/keys'
import { ringOf, isStageBody, stageKindedFeatures, useEditorStore, type EditorDoc } from '@/stores/editorStore'

import { reduce, type ReduceContext } from './tools/reducer'
import { DEFAULT_TOOL_CONFIG, type FeatureType, type ToolEvent } from './tools/types'
import { saveEditor } from './useAutosave'

/**
 * The editor's own keys, for both shells (expert `AnnotatorLayout` and `SimpleFixMode`):
 *
 * | Keys                                   | Does                                                  |
 * |----------------------------------------|-------------------------------------------------------|
 * | Ctrl+S · ⌘S                            | Save (also while typing in a field)                   |
 * | Ctrl+Z · ⌘Z                            | Undo — or, while drawing a shape/path, drop its last dot |
 * | Ctrl+Y · Ctrl+Shift+Z · ⌘⇧Z            | Redo                                                  |
 * | Esc                                    | Cancel a drag or a half-drawn shape; else clear the selection |
 * | Enter / Backspace (while drawing)      | Finish the shape / drop its last dot                  |
 *
 * Letters are matched on `event.key` when it is a Latin letter and on `event.code` otherwise, so
 * Ctrl+Z works on a Russian (Cyrillic) layout too while AZERTY keeps its own Z. Undo/redo never
 * fire while typing in a field (the field's own undo wins) or while a dialog, menu or list is
 * open; undo waits while a drag is in progress.
 *
 * The listener sits on `window` in the **capture** phase and stops the keys it handles, so an
 * older binding for the same keys (tinykeys, bubble phase) never runs a second undo. Mounting the
 * hook twice is harmless: only the first mounted instance listens.
 */

/* -------------------------------------------------------------------------- */
/* Feeding keys to the tool machine                                            */
/* -------------------------------------------------------------------------- */

function typeOfId(doc: EditorDoc | null, id: string): FeatureType | undefined {
  if (!isStageBody(doc)) return undefined
  if (doc.regions.some((r) => r.id === id)) return 'region'
  if (doc.anchors.some((a) => a.id === id)) return 'anchor'
  if (doc.portals.some((p) => p.id === id)) return 'portal'
  if (doc.paths.some((p) => p.id === id)) return 'path'
  if (doc.layers.some((l) => l.id === id)) return 'layer'
  return undefined
}

/** The reducer context the canvas would build, from the store alone (enough for keyboard events). */
function storeContext(): ReduceContext {
  const s = useEditorStore.getState()
  const doc = s.doc
  const horizonY = isStageBody(doc) && doc.depth.mode === 'horizon' ? doc.depth.horizon_y : undefined
  return {
    config: {
      ...DEFAULT_TOOL_CONFIG,
      viewport: s.viewport,
      activeKind: s.activeKind,
      selection: s.selection.ids,
      ringsById: (id) => ringOf(doc, id),
      horizonY,
      typeOf: (id) => typeOfId(doc, id),
      kindOf: (id) => (isStageBody(doc) ? stageKindedFeatures(doc).find((f) => f.id === id)?.kind : undefined),
    },
    viewport: s.viewport,
    armed: false,
    pointers: new Map(),
  }
}

/** Run one event through the tool machine exactly as the canvas does, and apply its effects. */
export function dispatchToolEvent(ev: ToolEvent): void {
  const s = useEditorStore.getState()
  const result = reduce({ tool: s.tool, state: s.toolState }, ev, storeContext())
  if (result.machine.tool !== s.tool) useEditorStore.getState().setTool(result.machine.tool)
  if (result.machine.state !== useEditorStore.getState().toolState) useEditorStore.getState().setToolState(result.machine.state)
  if (result.effects.length) useEditorStore.getState().applyEffects(result.effects)
}

export function dispatchToolKey(key: string, mods: { shift?: boolean; mod?: boolean; repeat?: boolean } = {}): void {
  dispatchToolEvent({ t: 'key', key, shift: mods.shift ?? false, mod: mods.mod ?? false, repeat: mods.repeat })
}

/** A polygon or a path is half drawn (its dots are not in the document yet). */
export function isDrawingOutline(): boolean {
  const t = useEditorStore.getState().toolState.t
  return t === 'polygon.drawing' || t === 'path.drawing'
}

/**
 * Undo, the way the buttons and Ctrl+Z mean it: while a shape is half drawn, drop its last dot;
 * during a drag, nothing; otherwise one step back. Returns whether anything happened.
 */
export function runUndo(): boolean {
  if (isDrawingOutline()) {
    dispatchToolKey('Backspace')
    return true
  }
  return useEditorStore.getState().undo()
}

/** Redo one step (nothing while a shape is half drawn or a drag is in progress). */
export function runRedo(): boolean {
  if (isDrawingOutline()) return false
  return useEditorStore.getState().redo()
}

/* -------------------------------------------------------------------------- */
/* The hook                                                                    */
/* -------------------------------------------------------------------------- */

let listening = 0
let latestQc: QueryClient | undefined

function onKeyDown(e: KeyboardEvent): void {
  if (e.defaultPrevented) return
  const st = useEditorStore.getState()
  if (!st.doc) return

  const cmd = editorCommandOf(e)
  if (cmd === 'save') {
    // Never the browser's "save this page" — and saving works from inside a field too.
    e.preventDefault()
    e.stopPropagation()
    if (!isOverlayActive(e)) void saveEditor(latestQc)
    return
  }
  if (cmd === 'undo' || cmd === 'redo') {
    if (isTypingTarget(e) || isOverlayActive(e)) return
    e.preventDefault()
    e.stopPropagation()
    if (cmd === 'undo') runUndo()
    else runRedo()
    return
  }

  if (e.ctrlKey || e.metaKey || e.altKey) return
  if (e.key !== 'Escape' && e.key !== 'Enter' && e.key !== 'Backspace') return
  if (isTypingTarget(e) || isOverlayActive(e)) return

  if (e.key === 'Escape') {
    if (st.toolState.t !== 'idle' || st.gesturing) {
      // Cancel the drag (rolled back, no undo step) or throw the half-drawn shape away.
      e.preventDefault()
      e.stopPropagation()
      dispatchToolKey('Escape', { shift: e.shiftKey })
      return
    }
    // Nothing in flight: Esc clears the selection (§9.3 cascade) and stays available to others.
    if (st.selection.ids.length) dispatchToolKey('Escape', { shift: e.shiftKey })
    return
  }

  // Enter finishes and Backspace un-draws — only while a polygon or a path is being drawn, and
  // then Backspace must not also delete the selected shape.
  if (isDrawingOutline()) {
    e.preventDefault()
    e.stopPropagation()
    dispatchToolKey(e.key, { shift: e.shiftKey, repeat: e.repeat })
  }
}

/** Bind Ctrl+S / Ctrl+Z / Ctrl+Y / Ctrl+Shift+Z / Esc (+ Enter/Backspace while drawing) for the open editor. */
export function useEditorCommands(opts: { enabled?: boolean } = {}): void {
  const { enabled = true } = opts
  const qc = useQueryClient()
  useEffect(() => {
    latestQc = qc
  }, [qc])
  useEffect(() => {
    if (!enabled) return
    listening += 1
    if (listening === 1) window.addEventListener('keydown', onKeyDown, true)
    return () => {
      listening -= 1
      if (listening === 0) window.removeEventListener('keydown', onKeyDown, true)
    }
  }, [enabled])
}
